import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import {
    Users, TrendingUp, TrendingDown, Minus, AlertTriangle,
    ClipboardList, Target, ChevronRight, Award, Activity,
} from 'lucide-react';
import api from '../../api';
import DashboardLayout from '../../layouts/DashboardLayout';
import TeamCaseload from '../../components/clinical/TeamCaseload';

// ─── Palette for per-reportee lines/bars ───────────────────────────────────
const COLORS = ['#4F46E5', '#0EA5E9', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];

// ─── Trend badge ────────────────────────────────────────────────────────────
const TrendBadge = ({ trend }) => {
    const map = {
        improving: { icon: <TrendingUp className="w-3.5 h-3.5" />, cls: 'bg-green-100 text-green-700', label: 'Improving' },
        declining:  { icon: <TrendingDown className="w-3.5 h-3.5" />, cls: 'bg-red-100 text-red-700',   label: 'Declining' },
        stable:     { icon: <Minus className="w-3.5 h-3.5" />,       cls: 'bg-secondary-100 text-secondary-500', label: 'Stable' },
    };
    const { icon, cls, label } = map[trend] || map.stable;
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold ${cls}`}>
            {icon}{label}
        </span>
    );
};

// ─── Score ring ─────────────────────────────────────────────────────────────
const ScoreRing = ({ score, scaleMax = 6 }) => {
    const pct = Math.round((score / scaleMax) * 100);
    const mid = scaleMax * 0.5;
    const color = score >= scaleMax * 0.67 ? '#10B981' : score >= mid ? '#F59E0B' : score > 0 ? '#EF4444' : '#CBD5E1';
    return (
        <div className="relative w-16 h-16 flex items-center justify-center flex-shrink-0">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 56 56">
                <circle cx="28" cy="28" r="24" fill="none" stroke="#E2E8F0" strokeWidth="5" />
                <circle
                    cx="28" cy="28" r="24" fill="none"
                    stroke={color} strokeWidth="5"
                    strokeDasharray={`${2 * Math.PI * 24}`}
                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - pct / 100)}`}
                    strokeLinecap="round"
                />
            </svg>
            <span className="text-sm font-bold text-secondary-900 z-10">
                {score > 0 ? score.toFixed(1) : '—'}
            </span>
        </div>
    );
};

// ─── Custom tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border border-secondary-100 shadow-lg rounded-lg p-3 text-sm">
            <p className="font-bold text-secondary-600 mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }} className="font-medium">
                    {p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
                </p>
            ))}
        </div>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────
const SupervisorDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.getSupervisorTeamOverview()
            .then(res => setData(res.data))
            .catch(() => setError('Failed to load team overview. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <DashboardLayout>
            <div className="p-12 text-center text-secondary-400 animate-pulse">Loading team overview...</div>
        </DashboardLayout>
    );
    if (error) return (
        <DashboardLayout>
            <div className="p-8 flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-5 h-5" />{error}
            </div>
        </DashboardLayout>
    );

    const { reportees, team_summary: ts, needs_attention, team_radar, scale_max: scaleMax = 6 } = data;
    // Domain names come from the API — varies by evaluation standard (CTSR vs NIMHANS)
    const domains = reportees.length > 0
        ? (reportees[0].domain_names || ['Structure', 'Cognitive', 'Behavioral', 'Relationship', 'Emotional'])
        : ['Structure', 'Cognitive', 'Behavioral', 'Relationship', 'Emotional'];

    // Build domain comparison bar data
    const domainBarData = domains.map(d => {
        const entry = { domain: d };
        reportees.forEach(r => {
            entry[r.name] = r.avg_domains?.[d] ?? 0;
        });
        return entry;
    });

    return (
        <DashboardLayout>
            <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">

                {/* ── Header ── */}
                <div>
                    <h1 className="text-2xl font-heading font-bold text-secondary-900 flex items-center gap-2">
                        <Activity className="w-6 h-6 text-primary-600" />
                        Team Overview
                    </h1>
                    <p className="text-secondary-500 mt-1 text-base">
                        Caseload and competency data across all clinicians under your supervision.
                    </p>
                </div>

                {/* ── Summary stat cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        label="Clinicians"
                        value={ts.total_reportees}
                        icon={<Users className="w-5 h-5 text-primary-500" />}
                        bg="bg-primary-50"
                    />
                    <StatCard
                        label="Total Sessions"
                        value={ts.total_sessions}
                        icon={<ClipboardList className="w-5 h-5 text-indigo-500" />}
                        bg="bg-indigo-50"
                    />
                    <StatCard
                        label="Team Avg Score"
                        value={ts.team_overall_score > 0 ? `${ts.team_overall_score} / ${scaleMax}` : '—'}
                        icon={<Award className="w-5 h-5 text-amber-500" />}
                        bg="bg-amber-50"
                    />
                    <StatCard
                        label="Need Attention"
                        value={ts.needs_attention_count}
                        icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
                        bg="bg-red-50"
                        highlight={ts.needs_attention_count > 0}
                    />
                </div>

                {/* ── Caseload (patients, sessions, notes waiting; feature "supervision") ── */}
                <TeamCaseload />

                {/* ── Needs Attention strip ── */}
                {needs_attention.length > 0 && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                            <h3 className="text-base font-bold text-red-700 uppercase tracking-wide">Needs Your Attention</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {needs_attention.map(r => (
                                <button
                                    key={r.reportee_id}
                                    onClick={() => navigate(`/ConsultationHistory?doctor_id=${r.reportee_id}&status=pending`)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-red-200 rounded-lg text-sm text-red-700 hover:bg-red-100 transition-colors"
                                >
                                    <span className="font-medium">{r.name}</span>
                                    <span className="text-xs text-red-400 capitalize">
                                        ({r.reason === 'declining' ? 'Declining trend' : 'Low score'})
                                    </span>
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Charts row ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Team average radar */}
                    <div className="bg-white rounded-xl border border-secondary-100 shadow-sm p-6">
                        <h3 className="text-base font-bold text-secondary-500 uppercase tracking-wide mb-4">
                            Team Average — Domain Radar
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={team_radar}>
                                    <PolarGrid />
                                    <PolarAngleAxis dataKey="domain" tick={{ fontSize: 13 }} />
                                    <PolarRadiusAxis angle={30} domain={[0, scaleMax]} tick={{ fontSize: 11 }} />
                                    <Radar
                                        name="Team Avg"
                                        dataKey="score"
                                        stroke="#4F46E5"
                                        fill="#4F46E5"
                                        fillOpacity={0.25}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Domain comparison bar */}
                    <div className="bg-white rounded-xl border border-secondary-100 shadow-sm p-6">
                        <h3 className="text-base font-bold text-secondary-500 uppercase tracking-wide mb-4">
                            Per-Domain Comparison
                        </h3>
                        {reportees.length === 0 ? (
                            <div className="h-64 flex items-center justify-center text-secondary-400 text-sm">No data yet</div>
                        ) : (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={domainBarData} margin={{ left: -10 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                        <XAxis dataKey="domain" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                        <YAxis domain={[0, scaleMax]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 13 }} />
                                        {reportees.map((r, idx) => (
                                            <Bar
                                                key={r.reportee_id}
                                                dataKey={r.name}
                                                fill={COLORS[idx % COLORS.length]}
                                                radius={[3, 3, 0, 0]}
                                                maxBarSize={20}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Reportee table ── */}
                <div className="bg-white rounded-xl border border-secondary-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-secondary-100 flex items-center justify-between">
                        <h3 className="font-bold text-secondary-800 flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary-500" />
                            Individual Performance
                        </h3>
                        <button
                            onClick={() => navigate('/supervisor/reportees')}
                            className="text-xs text-primary-600 hover:underline flex items-center gap-1"
                        >
                            View team list <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {reportees.length === 0 ? (
                        <div className="p-12 text-center text-secondary-400 text-sm">
                            No reportees assigned yet.
                        </div>
                    ) : (
                        <div className="divide-y divide-secondary-50">
                            {reportees
                                .slice()
                                .sort((a, b) => b.overall_score - a.overall_score)
                                .map((r, idx) => (
                                    <ReporteeRow
                                        key={r.reportee_id}
                                        reportee={r}
                                        rank={idx + 1}
                                        color={COLORS[idx % COLORS.length]}
                                        domains={domains}
                                        onSessions={() => navigate(`/ConsultationHistory?doctor_id=${r.reportee_id}`)}
                                        onAnalytics={() => navigate(`/therapist-dashboard?doctor_id=${r.reportee_id}`)}
                                        scaleMax={r.scale_max || scaleMax}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

// ─── Sub-components ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, bg, highlight }) => (
    <div className={`${bg} rounded-xl p-5 flex items-center gap-4 ${highlight ? 'ring-1 ring-red-200' : ''}`}>
        <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center shadow-sm">
            {icon}
        </div>
        <div>
            <p className="text-2xl font-bold text-secondary-900">{value}</p>
            <p className="text-sm text-secondary-500 font-medium">{label}</p>
        </div>
    </div>
);

const ReporteeRow = ({ reportee: r, rank, color, domains, onSessions, onAnalytics, scaleMax = 6 }) => (
    <div className="px-6 py-5 flex flex-col md:flex-row md:items-center gap-4">

        {/* Rank + Avatar + Name */}
        <div className="flex items-center gap-3 w-56 flex-shrink-0">
            <span className="text-sm font-bold text-secondary-300 w-5 text-center">{rank}</span>
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
                style={{ background: color }}
            >
                {r.name?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
                <p className="font-bold text-secondary-800 truncate text-base">{r.name}</p>
                <p className="text-sm text-secondary-400 truncate">{r.evaluated_sessions} / {r.total_sessions} evaluated</p>
            </div>
        </div>

        {/* Score ring */}
        <ScoreRing score={r.overall_score} scaleMax={scaleMax} />

        {/* Domain mini-bars */}
        <div className="flex-1 grid grid-cols-5 gap-2">
            {domains.map(d => {
                const val = r.avg_domains?.[d] ?? 0;
                const pct = Math.round((val / scaleMax) * 100);
                const barColor = val >= 4 ? '#10B981' : val >= 3 ? '#F59E0B' : val > 0 ? '#EF4444' : '#E2E8F0';
                return (
                    <div key={d} className="flex flex-col items-center gap-1">
                        <div className="w-full h-1.5 bg-secondary-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                        <span className="text-xs text-secondary-400 text-center leading-tight">{d}</span>
                        <span className="text-xs font-bold text-secondary-600">{val > 0 ? val.toFixed(1) : '—'}</span>
                    </div>
                );
            })}
        </div>

        {/* Trend + weakest */}
        <div className="flex flex-col gap-1.5 w-36 flex-shrink-0">
            <TrendBadge trend={r.trend} />
            {r.weakest_domain && (
                <span className="text-xs text-secondary-400">
                    Weakest: <span className="font-semibold text-secondary-600">{r.weakest_domain}</span>
                </span>
            )}
            <span className="text-xs text-secondary-400">
                {r.near_miss_count} near miss{r.near_miss_count !== 1 ? 'es' : ''}
            </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0">
            <button
                onClick={onSessions}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 text-sm font-medium transition-colors"
            >
                <ClipboardList className="w-4 h-4" /> Sessions
            </button>
            <button
                onClick={onAnalytics}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-medium transition-colors"
            >
                <TrendingUp className="w-4 h-4" /> Analytics
            </button>
        </div>
    </div>
);

export default SupervisorDashboard;
